import { hasWindow } from "@omni/utils/dom.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles/tw.merge";

import { InputGroup } from "@/components/Form/Base/inputGroup.component";
import { StoreSearchInput } from "@/components/Store/StoreSearchInput/storeSearchInput";
import {
    SearchItem,
    SearchItemType
} from "@/components/Store/StoreSearchInput/storeSearchInput.types";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useWoosmapServices } from "@/utils/hooks/useWoosmapServices.hook";

import {
    STORE_DIRECTIONS_MESSAGE_ID,
    STORE_SEARCH_INPUT_TYPES
} from "../../storeDirections.config";
import { useStoreDirectionsContext } from "../../storeDirections.context";
import {
    fetchLocalityDetails,
    fetchLocalityGeocode
} from "./storeDirectionsInput.helpers";

const StoreDirectionsInput = () => {
    const { t } = useGranite();

    const { origin, setOrigin, resetOrigin } = useStoreDirectionsContext();

    const { LocalitiesService } = useWoosmapServices();

    const handleOnUseLocation = (latitude: number, longitude: number) => {
        if (!isDefined(LocalitiesService)) return;

        clearMessageById(STORE_DIRECTIONS_MESSAGE_ID);

        fetchLocalityGeocode(LocalitiesService, {
            lat: latitude,
            lng: longitude
        })
            .then((result) => ({
                value: result.public_id,
                latitude: result.geometry?.location.lat,
                longitude: result.geometry?.location.lng,
                label: t("form.storeDirections.myLocation"),
                type: SearchItemType.Locality
            }))
            .then(setOrigin)
            .catch(handleOnLocalityNotFoundError);
    };

    const handleOnSelect = (searchItem: SearchItem | null) => {
        if (!isDefined(LocalitiesService)) return;

        clearMessageById(STORE_DIRECTIONS_MESSAGE_ID);

        if (!isDefined(searchItem)) return;

        fetchLocalityDetails(LocalitiesService, searchItem.value)
            .then((result) => ({
                ...searchItem,
                latitude: result.geometry?.location.lat,
                longitude: result.geometry?.location.lng
            }))
            .then(setOrigin)
            .catch(handleOnLocalityNotFoundError);
    };

    const handleOnLocalityNotFoundError = () =>
        broadcastMessage({
            id: STORE_DIRECTIONS_MESSAGE_ID,
            text: t("storesearch.locality.notFound"),
            status: "danger",
            dismissible: true
        });

    return (
        <StoreSearchInput
            clearable={true}
            menuPortalTarget={hasWindow() ? document.body : null}
            messageId={STORE_DIRECTIONS_MESSAGE_ID}
            value={origin}
            itemsToRetrieve={STORE_SEARCH_INPUT_TYPES}
            placeholder={t("form.storeDirections.input.placeholder")}
            loadingText={t("form.storeDirections.loading")}
            aria-label={t("accessibility.storeDirections.input.label")}
            onClearValue={resetOrigin}
            onSelect={handleOnSelect}
            onUseLocation={handleOnUseLocation}
        >
            <InputGroup>
                <StoreSearchInput.Input className={cn("tw:w-full")} />
                <StoreSearchInput.SearchButton className={cn("tw:min-w-fit")}>
                    {t("form.storeDirections.showRoute")}
                </StoreSearchInput.SearchButton>
            </InputGroup>
            <StoreSearchInput.Location />
        </StoreSearchInput>
    );
};

export { StoreDirectionsInput };
